import { useState, useEffect, useContext, lazy, Suspense } from 'react';
import { Tabs, Tab, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { TabPanel, BrowserTitle, Spinner } from '../components';
import { AuthContext } from '../context/AuthProvider';

const SettingsTable = lazy(() => import('../components/Tables/Settings/settings.settings'));
const UsersTable = lazy(() => import('../components/Tables/Settings/settings.users'));
const SuppliersTable = lazy(() => import('../components/Tables/Settings/settings.suppliers'));
const AppointmentTable = lazy(() => import('../components/Tables/Settings/settings.appointment'));
const PropertyTable = lazy(() => import('../components/Tables/Settings/settings.propertyTypes'));
const IntegrationsSettings = lazy(() => import('../components/Tables/Settings/settings.integrations'));
const WorkHoursSettings = lazy(() => import('../components/Tables/Settings/settings.workHours'));
const LowPriceGuardSettings = lazy(() => import('../components/Tables/Settings/settings.lowPriceGuard'));
const SupplyRulesSettings = lazy(() => import('../components/Tables/Settings/settings.supplyRules'));
const KitRulesSettings = lazy(() => import('../components/Tables/Settings/settings.kitRules'));
const PrintersSettings = lazy(() => import('../components/Tables/Settings/settings.printers'));

function addTabIndex(index) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

const tabStyle = { color: '#0D3B59 !important', fontFamily: 'Lato, sans-serif' };

export const Settings = () => {
  const location = window.location.pathname;
  const history = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const { userData } = useContext(AuthContext);
  // Server printers manage CUPS on the host — admins only.
  const showPrinters = ['admin', 'superadmin'].includes(userData?.role);

  const tabSwitcher = (e, val) => {
    e.preventDefault();
    setActiveTab(val);

    switch (val) {
      case 1:
        history('/settings/users');
        break;
      case 2:
        history('/settings/suppliers');
        break;
      case 3:
        history('/settings/appointment');
        break;
      case 4:
        history('/settings/property');
        break;
      case 5:
        history('/settings/integrations');
        break;
      case 6:
        history('/settings/work-hours');
        break;
      case 7:
        history('/settings/scheduling');
        break;
      case 8:
        history('/settings/auto-supplies');
        break;
      case 9:
        history('/settings/kits');
        break;
      case 10:
        history('/settings/printers');
        break;
      default:
        history('/settings');
        break;
    }
  };

  useEffect(() => {
    switch (location) {
      case '/settings/users':
        setActiveTab(1);
        break;
      case '/settings/suppliers':
        setActiveTab(2);
        break;
      case '/settings/appointment':
        setActiveTab(3);
        break;
      case '/settings/property':
        setActiveTab(4);
        break;
      case '/settings/integrations':
        setActiveTab(5);
        break;
      case '/settings/work-hours':
        setActiveTab(6);
        break;
      case '/settings/scheduling':
        setActiveTab(7);
        break;
      case '/settings/auto-supplies':
        setActiveTab(8);
        break;
      case '/settings/kits':
        setActiveTab(9);
        break;
      case '/settings/printers':
        setActiveTab(10);
        break;
      default:
        setActiveTab(0);
        break;
    }
  }, [location]);

  return (
    <Box component="section" id="settings-page">
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={tabSwitcher}
            aria-label="settings tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Settings" {...addTabIndex(0)} sx={tabStyle} />
            <Tab label="Users" {...addTabIndex(1)} sx={tabStyle} />
            <Tab label="Suppliers" {...addTabIndex(2)} sx={tabStyle} />
            <Tab label="Appointment" {...addTabIndex(3)} sx={tabStyle} />
            <Tab label="Property" {...addTabIndex(4)} sx={tabStyle} />
            <Tab label="Integrations" {...addTabIndex(5)} sx={tabStyle} />
            <Tab label="Work Hours" {...addTabIndex(6)} sx={tabStyle} />
            <Tab label="Scheduling" {...addTabIndex(7)} sx={tabStyle} />
            <Tab label="Auto-Supplies" {...addTabIndex(8)} sx={tabStyle} />
            <Tab label="Mini Split Kits" {...addTabIndex(9)} sx={tabStyle} />
            {showPrinters && <Tab label="Printers" {...addTabIndex(10)} sx={tabStyle} />}
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          {activeTab === 0 && <BrowserTitle title="Settings | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <SettingsTable />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {activeTab === 1 && <BrowserTitle title="Users | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <UsersTable />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          {activeTab === 2 && <BrowserTitle title="Suppliers | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <SuppliersTable />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          {activeTab === 3 && <BrowserTitle title="Appointment | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <AppointmentTable />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          {activeTab === 4 && <BrowserTitle title="Property | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <PropertyTable />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          {activeTab === 5 && <BrowserTitle title="Integrations | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <IntegrationsSettings />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={6}>
          {activeTab === 6 && <BrowserTitle title="Work Hours | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <WorkHoursSettings />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={7}>
          {activeTab === 7 && <BrowserTitle title="Scheduling | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <LowPriceGuardSettings />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={8}>
          {activeTab === 8 && <BrowserTitle title="Auto-Supplies | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <SupplyRulesSettings />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={9}>
          {activeTab === 9 && <BrowserTitle title="Mini Split Kits | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <KitRulesSettings />
          </Suspense>
        </TabPanel>

        {showPrinters && (
          <TabPanel value={activeTab} index={10}>
            {activeTab === 10 && <BrowserTitle title="Printers | Airmaxx Pro" />}
            <Suspense fallback={<Spinner />}>
              <PrintersSettings />
            </Suspense>
          </TabPanel>
        )}
      </Box>
    </Box>
  );
};
